import { constants, locale } from '@trqk/utils';

import {
    InvalidStatement,
    DuplicateStatement,
    CouldNotIngest,
} from '../components/toastMessages';

const { API_ERROR_MESSAGES } = constants;

/**
 * @typedef {GetIngestionToastArg}
 * @property {string} error A single error message for this ingestion, either from the failed
 * `addStatementToTrqkUser` mutation or from the statementView.status_name
 * @property {Maybe<string>} filename the file name
 * @property {Maybe<string>} statementUrl the statement URL
 * @property {TrqkStatementView | null} trqkStatementView the statement view, if it exists
 * @property {string[]} failureMessages an array of status_message values from log event objects
 * which indicated errors during ingestion
 */
interface GetIngestionToastArg {
    error: string;
    filename: Maybe<string>;
    statementUrl: Maybe<string>;
    statementView: TrqkStatementView | null;
    failureMessages?: string[];
}

/**
 * Get the input for a toast shown when the user clicks the "Something's wrong" link in a FailureRow
 * @param {GetIngestionToastArg} input
 */
export function getIngestionToastInput({
    error,
    failureMessages,
    filename,
    statementUrl,
    statementView,
}: GetIngestionToastArg): AddToastInput<IngestionToastInput> {
    const allFailureMessages = failureMessages ? failureMessages.join(' ') : '';
    const allErrors = `${error} ${allFailureMessages}`;

    if (allErrors.includes(API_ERROR_MESSAGES.NOT_A_VALID_STATEMENT)) {
        return {
            kind: 'warning-card',
            RenderContent: InvalidStatement,
            persist: true,
        };
    }

    if (
        allErrors.includes(API_ERROR_MESSAGES.STATEMENT_PREVIOUSLY_ADDED) ||
        allErrors.includes(API_ERROR_MESSAGES.DUPLICATE_OF_STATEMENT) ||
        allErrors.includes(API_ERROR_MESSAGES.ALREADY_UPLOADED_THIS_STATEMENT)
    ) {
        return {
            kind: 'warning-card',
            RenderContent: DuplicateStatement,
            persist: true,
        };
    }

    if (allErrors.includes(API_ERROR_MESSAGES.INGESTION_TIMEOUT)) {
        return {
            kind: 'warning-card',
            RenderContent: CouldNotIngest,
            renderContentInput: {
                canRetry: false,
                error,
                filename,
                statementUrl,
                statementView,
                title: locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE
                    .COULD_NOT_INGEST.TITLE.TOOK_TOO_LONG,
            },
            persist: true,
        };
    }

    return {
        kind: 'warning-card',
        RenderContent: CouldNotIngest,
        renderContentInput: {
            canRetry: true,
            error,
            failureMessages,
            filename,
            statementUrl,
            statementView,
            title: locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE
                .COULD_NOT_INGEST.TITLE.NOT_ABLE_TO_INGEST,
        },
        persist: true,
    };
}
